import { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ReactSelect from 'react-select'
import { getAllOrganizations } from '../../../../actions/organization/organizationsgetall'
import {
	codeListGet,
	groupsGetAll,
	personGroupGet,
	personGroupUpdate,
	personTeamsGet,
	personTeamsUpdate,
	teamsGetAll,
} from '../../../../api'
import CheckboxOption from '../../../../common/CheckboxOption'
import DimensionSelectionSection from '../../../../common/DimensionSelectionSection'
import { PersonSelectSupervisor } from '../../../../common/personselectsupervisor'
import { reactSelectSearchSettings } from '../../../../common/reactselect'
import { jsError } from '../../../../common/utils'
import { activeIntegrationsSelector, authSelector, organizationsSelector } from '../../../../reducers'

export function OrganizationalData({
	person,
	setPerson,
	handleControlDetails,
	selectChange,
	fieldParams,
	formErrors,
	isPersonal,
	handleDimensionAllocChange,
}) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const { role, companyBusinessId } = useSelector(authSelector)
	const supervisor = role === 'SUPERVISOR'

	const [costUnitOptions, setCostUnitOptions] = useState([])
	const [costUnit, setCostUnit] = useState()

	const [dropdownTeams, setDropdownTeams] = useState([])
	const [dropdownGroups, setDropdownGroups] = useState([])

	const organizations = useSelector(organizationsSelector)
	const activeintegrations = useSelector(activeIntegrationsSelector)
	const isMulti = activeintegrations?.tokenProcountor // if Procountor, allow multi-select

	const mapToOptions = useCallback(
		(data) =>
			data.items.map((item) => ({
				value: item.codeKey,
				label: isMulti ? item.codeValue : `${item.codeKey} - ${item.codeValue}`, // if Procountor, do not show dimension ID in label
				codeValue: item.codeValue,
				codeKey: item.codeKey,
				active: item.memo !== 'P',
			})),
		[isMulti],
	)

	const [projectOptions, setProjectOptions] = useState([])
	const [project, setProject] = useState()

	const [dim3Options, setDim3Options] = useState([])
	const [dim3, setDim3] = useState()

	const [dim4Options, setDim4Options] = useState([])
	const [dim4, setDim4] = useState()

	const [dim5Options, setDim5Options] = useState([])
	const [dim5, setDim5] = useState()
	/*
  const [dim6Options, setDim6Options] = useState([]);
  const [dim6, setDim6] = useState();
*/

	const [personTeams, setPersonTeams] = useState([])
	const [personGroup, setPersonGroup] = useState(null)

	const syncPersonTeamData = useCallback(
		(teams) => {
			setPerson((currentPerson) => ({
				...currentPerson,
				controlDetails: {
					...currentPerson.controlDetails,
					teams,
					teamMemberships: teams,
				},
			}))
		},
		[setPerson],
	)

	const syncPersonGroupData = useCallback(
		(groupData) => {
			setPerson((currentPerson) => ({
				...currentPerson,
				controlDetails: {
					...currentPerson.controlDetails,
					employeeGroupId: groupData?.employeeGroupId || null,
					employeeGroupCode: groupData?.employeeGroupCode || null,
					employeeGroupName: groupData?.employeeGroupName || null,
				},
			}))
		},
		[setPerson],
	)

	const fetchCodeLists = useCallback(async () => {
		let res

		try {
			res = await codeListGet('costUnits')
			setCostUnit({ name: res.data.name, dimensionId: res.data.memo })
			setCostUnitOptions(mapToOptions(res.data))

			res = await codeListGet('projects')
			setProject({ name: res.data.name, dimensionId: res.data.memo })
			setProjectOptions(mapToOptions(res.data))

			res = await codeListGet('dim3')
			setDim3({ name: res.data.name, dimensionId: res.data.memo })
			setDim3Options(mapToOptions(res.data))

			res = await codeListGet('dim4')
			setDim4({ name: res.data.name, dimensionId: res.data.memo })
			setDim4Options(mapToOptions(res.data))

			res = await codeListGet('dim5')
			setDim5({ name: res.data.name, dimensionId: res.data.memo })
			setDim5Options(mapToOptions(res.data))

			/* // Sixth dimension is organization selection
      res = await codeListGet("dim6");
      setDim6({ name: res.data.name, dimensionId: res.data.memo });
      setDim6Options(mapToOptions(res.data));
      */
		} catch {
			// if no dimension codelists, import them from eSalary
			jsError('Error getting dimensions')
		}
	}, [mapToOptions])

	const fetchTeams = useCallback(async () => {
		try {
			const res = await teamsGetAll()
			const data = res.data || []

			setDropdownTeams(
				data.map((t) => ({
					id: t.id,
					teamName: t.teamName,
					teamColor: t.color,
					active: t.active,
				})),
			)
		} catch (err) {
			console.error('Failed to fetch teams', err)
		}
	}, [])

	const fetchGroups = useCallback(async () => {
		try {
			const res = await groupsGetAll()
			const data = res.data || []

			setDropdownGroups(
				data.map((g) => ({
					id: g.id,
					name: g.name,
					colour: g.colour || '#003a78',
					active: g.active,
				})),
			)
		} catch (err) {
			console.error('Failed to fetch employee groups', err)
		}
	}, [])

	useEffect(() => {
		fetchCodeLists()
		fetchTeams()
		fetchGroups()
		dispatch(getAllOrganizations()) // Fetch organizations
	}, [fetchCodeLists, fetchTeams, fetchGroups, dispatch])

	const organizationOptions = organizations
		? organizations.map((organization) => ({
				value: organization.businessIdentifier,
				label: `${organization.organizationName} - ${organization.businessIdentifier}`,
				id: organization.id,
				businessIdentifier: organization.businessIdentifier,
			}))
		: []

	// react-select
	const selectSupervisor = (newValue, actionMeta) =>
		handleControlDetails({
			target: {
				id: actionMeta.name,
				value: { personNumber: newValue?.value || newValue },
			},
		}) // value or null

	const DIMENSION1_KEY = 'costUnits'
	const DIMENSION2_KEY = 'projects'
	const DIMENSION3_KEY = 'dim3'
	const DIMENSION4_KEY = 'dim4'
	const DIMENSION5_KEY = 'dim5'
	//const DIMENSION6_KEY = "dim6";

	function useDimension(person, dimensionKey) {
		const dimFromPerson = Array.isArray(person?.dimensions)
			? person.dimensions.find((d) => d?.codeKey === dimensionKey)
			: null

		const mapFromPerson = useCallback(() => {
			const items = Array.isArray(dimFromPerson?.items) ? dimFromPerson.items : []
			return items.map((it) => ({
				value: String(it.codeKey),
				label: isMulti ? it.codeValue : `${it.codeKey}${it.codeValue ? ` - ${it.codeValue}` : ''}`, // if Procountor, do not show dimension ID in label
				codeKey: String(it.codeKey),
				codeValue: it.codeValue ?? '',
				percent: Number(it.percent) || 0,
			}))
		}, [dimFromPerson, isMulti])

		const [prevDimFromPerson, setPrevDimFromPerson] = useState(dimFromPerson)
		const [selectedItems, setSelectedItems] = useState(() => mapFromPerson())

		// Derived state: sync when the server-side dimension data for this key changes.
		// Comparing by reference during render avoids the infinite-loop risk of
		// useEffect with an unstable mapFromPerson dependency.
		if (prevDimFromPerson !== dimFromPerson) {
			setPrevDimFromPerson(dimFromPerson)
			setSelectedItems(mapFromPerson())
		}

		const isAllocValid = useCallback((selected, sum) => {
			const len = Array.isArray(selected) ? selected.length : 0
			const s = Number(sum) || 0
			return s === 100 || len === 0 || (len === 1 && s === 0)
		}, [])

		const allocSum = useMemo(
			() => (selectedItems || []).reduce((a, o) => a + (Number(o.percent) || 0), 0),
			[selectedItems],
		)

		const allocValid = useMemo(() => isAllocValid(selectedItems, allocSum), [selectedItems, allocSum, isAllocValid])

		return {
			selectedItems,
			setSelectedItems,
			dimensionAllocSum: allocSum,
			dimensionAllocValid: allocValid,
		}
	}

	// dim1
	const {
		selectedItems: selectedDimension1Items,
		setSelectedItems: setSelectedDimension1Items,
		dimensionAllocSum: dimension1AllocSum,
		dimensionAllocValid: dimension1AllocValid,
	} = useDimension(person, DIMENSION1_KEY)

	// dim2
	const {
		selectedItems: selectedDimension2Items,
		setSelectedItems: setSelectedDimension2Items,
		dimensionAllocSum: dimension2AllocSum,
		dimensionAllocValid: dimension2AllocValid,
	} = useDimension(person, DIMENSION2_KEY)

	// dim3
	const {
		selectedItems: selectedDimension3Items,
		setSelectedItems: setSelectedDimension3Items,
		dimensionAllocSum: dimension3AllocSum,
		dimensionAllocValid: dimension3AllocValid,
	} = useDimension(person, DIMENSION3_KEY)

	// dim4
	const {
		selectedItems: selectedDimension4Items,
		setSelectedItems: setSelectedDimension4Items,
		dimensionAllocSum: dimension4AllocSum,
		dimensionAllocValid: dimension4AllocValid,
	} = useDimension(person, DIMENSION4_KEY)

	// dim5
	const {
		selectedItems: selectedDimension5Items,
		setSelectedItems: setSelectedDimension5Items,
		dimensionAllocSum: dimension5AllocSum,
		dimensionAllocValid: dimension5AllocValid,
	} = useDimension(person, DIMENSION5_KEY)

	// dim6 (Business identifier String)
	/*
  const {
    selectedItems: selectedDimension6Items,
    setSelectedItems: setSelectedDimension6Items,
    dimensionAllocSum: dimension6AllocSum,
    dimensionAllocValid: dimension6AllocValid,
  } = useDimension(person, DIMENSION6_KEY);
*/

	useEffect(() => {
		async function fetchPersonTeams() {
			if (!person?.personId) return

			try {
				const res = await personTeamsGet(person.personId)
				const teams = res.data.assignedTeams || []

				setPersonTeams(teams)
				syncPersonTeamData(teams)
			} catch (err) {
				console.error('Failed to fetch person teams', err)
			}
		}

		async function fetchPersonGroup() {
			if (!person?.personId) return
			try {
				const res = await personGroupGet(person.personId)
				if (res.data) {
					setPersonGroup(res.data)
					syncPersonGroupData(res.data)
				} else {
					setPersonGroup(null)
					syncPersonGroupData(null)
				}
			} catch (err) {
				console.error('Failed to fetch person employee group', err)
			}
		}

		fetchPersonTeams()
		fetchPersonGroup()
	}, [person?.personId, syncPersonTeamData, syncPersonGroupData])

	return (
		<>
			<Row>
				<Form.Group as={Col} xl={3} controlId="payGroup">
					<Form.Label>{t('PayGroup', 'Maksuryhmä (palkanlaskennassa)')}</Form.Label>
					{/* TODO replace this with person search? */}
					<Form.Control
						onChange={handleControlDetails}
						defaultValue={person.controlDetails?.payGroup}
						isInvalid={!!formErrors.payGroup}
						maxLength={fieldParams.payGroup?.maxLength}
						disabled={isPersonal}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.payGroup}</Form.Control.Feedback>
				</Form.Group>

				<Col xl={4}>
					<PersonSelectSupervisor
						isPersonal={isPersonal}
						disabled={supervisor || isPersonal}
						controlId="supervisor"
						label={t('Supervisor', 'Esihenkilö')}
						excludedNumbers={[person.controlDetails?.supervisor2?.personNumber]}
						selectedPerson={person.controlDetails?.supervisor}
						selectChange={selectSupervisor}
					/>
				</Col>

				{!!person.controlDetails?.supervisor && (
					<Col xl={4}>
						<PersonSelectSupervisor
							isPersonal={isPersonal}
							disabled={supervisor || isPersonal}
							controlId="supervisor2"
							label={`${t('Supervisor', 'Esihenkilö')} 2`}
							excludedNumbers={[person.controlDetails?.supervisor?.personNumber]}
							selectedPerson={person.controlDetails?.supervisor2}
							selectChange={selectSupervisor}
						/>
					</Col>
				)}
			</Row>

			<Row className="align-items-start mb-3">
				<Form.Label style={{ background: 'none' }}>{t('EmployeeGroup', 'Työntekijäryhmä')}</Form.Label>

				<Col xl={4}>
					<ReactSelect
						isMulti={false}
						isClearable
						options={
							dropdownGroups
								?.filter((g) => g.active)
								.map((g) => ({
									value: g.id,
									label: g.name,
								})) || []
						}
						value={
							personGroup?.employeeGroupId
								? {
										value: personGroup.employeeGroupId,
										label: dropdownGroups.find((g) => g.id === personGroup.employeeGroupId)?.name || '',
									}
								: null
						}
						onChange={async (selected) => {
							if (!person?.personId) return

							const newGroupId = selected?.value || null

							const assignmentPayload = {
								employeeGroupId: newGroupId,
								startDate: new Date().toISOString().slice(0, 10),
								endDate: null,
							}

							try {
								await personGroupUpdate(person.personId, assignmentPayload)
								const res = await personGroupGet(person.personId)

								const updatedGroupData = res.data || null
								setPersonGroup(updatedGroupData)

								syncPersonGroupData(updatedGroupData)
							} catch (err) {
								console.error('Failed to update person employee group', err)
							}
						}}
						placeholder={t('SelectGroup', 'Valitse ryhmä')}
						{...reactSelectSearchSettings(t)}
					/>
				</Col>

				<Col xl={6} className="d-flex flex-wrap align-items-center">
					{personGroup?.employeeGroupId ? (
						(() => {
							const matchedGroup = dropdownGroups.find((g) => g.id === personGroup.employeeGroupId)
							const bgColor = matchedGroup?.colour || '#888'
							const groupName = matchedGroup?.name || personGroup.employeeGroupName || ''

							return (
								<Badge
									key={personGroup.employeeGroupId}
									bg={bgColor}
									style={{
										backgroundColor: bgColor,
										color: '#fff',
										marginRight: 6,
										marginTop: 6,
										padding: '6px 16px',
										fontSize: '0.835rem',
									}}
								>
									{groupName}
								</Badge>
							)
						})()
					) : (
						<span style={{ color: '#888' }}>{t('NoSelectedGroup', 'Ei työntekijäryhmää valittuna')}</span>
					)}
				</Col>
			</Row>

			<Row className="align-items-start">
				<Form.Label style={{ background: 'none' }}>{t('Teams', 'Tiimit')}</Form.Label>

				<Col xl={4}>
					<ReactSelect
						isMulti
						options={
							dropdownTeams?.map((t) => ({
								value: t.id,
								label: t.teamName,
							})) || []
						}
						value={dropdownTeams
							.filter((t) => personTeams.some((pt) => pt.teamId === t.id))
							.map((t) => ({
								value: t.id,
								label: t.teamName,
							}))}
						onChange={async (selected) => {
							if (!person?.personId) return

							const newIds = selected?.map((s) => s.value) || []

							const assignments = newIds.map((id) => ({
								teamId: id,
								startDate: new Date().toISOString().slice(0, 10),
								endDate: null,
							}))

							try {
								await personTeamsUpdate(person.personId, assignments)
								const res = await personTeamsGet(person.personId)
								const teams = res.data.assignedTeams || []
								setPersonTeams(teams)
								syncPersonTeamData(teams)
							} catch (err) {
								console.error('Failed to update person teams', err)
							}
						}}
						closeMenuOnSelect={false}
						placeholder={t('SelectTeam', 'Valitse tiimi')}
						{...reactSelectSearchSettings(t)}
					/>
				</Col>

				<Col xl={6} className="d-flex flex-wrap align-items-center">
					{personTeams?.length > 0 ? (
						personTeams.map((team) => {
							const bgColor = team.color || dropdownTeams.find((t) => t.id === team.teamId)?.teamColor || '#888'

							return (
								<Badge
									key={team.teamId}
									bg={bgColor}
									style={{
										backgroundColor: bgColor,
										color: '#fff',
										marginRight: 6,
										marginTop: 6,
										padding: '6px 16px',
										fontSize: '0.835rem',
									}}
								>
									{team.name}
								</Badge>
							)
						})
					) : (
						<span style={{ color: '#888' }}>{t('NoSelectedTeams', 'Ei tiimejä valittuna')}</span>
					)}
				</Col>
			</Row>

			{/* TODO: Add isProcountor check for isMulti */}
			{costUnit && (
				<DimensionSelectionSection
					t={t}
					dimensionKey={DIMENSION1_KEY}
					dimensionName={costUnit?.name}
					dimensionId={costUnit?.dimensionId}
					dimensionOptions={costUnitOptions}
					selectedItems={selectedDimension1Items}
					setSelectedItems={setSelectedDimension1Items}
					errorText={formErrors.dimension1}
					isPersonal={isPersonal}
					disabled={isPersonal}
					isMulti={isMulti}
					dimensionAllocSum={dimension1AllocSum}
					dimensionAllocValid={dimension1AllocValid}
					handleDimensionAllocChange={handleDimensionAllocChange}
					CheckboxOption={CheckboxOption}
					reactSelectSearchSettings={reactSelectSearchSettings}
				/>
			)}

			{project && (
				<DimensionSelectionSection
					t={t}
					dimensionKey={DIMENSION2_KEY}
					dimensionName={project?.name ?? ''}
					dimensionId={project?.dimensionId ?? ''}
					dimensionOptions={projectOptions}
					selectedItems={selectedDimension2Items}
					setSelectedItems={setSelectedDimension2Items}
					errorText={formErrors.dimension2}
					isPersonal={isPersonal}
					disabled={isPersonal}
					isMulti={isMulti}
					dimensionAllocSum={dimension2AllocSum}
					dimensionAllocValid={dimension2AllocValid}
					handleDimensionAllocChange={handleDimensionAllocChange}
					CheckboxOption={CheckboxOption}
					reactSelectSearchSettings={reactSelectSearchSettings}
				/>
			)}

			{dim3 && (
				<DimensionSelectionSection
					t={t}
					dimensionKey={DIMENSION3_KEY}
					dimensionName={dim3?.name ?? ''}
					dimensionId={dim3?.dimensionId ?? ''}
					dimensionOptions={dim3Options}
					selectedItems={selectedDimension3Items}
					setSelectedItems={setSelectedDimension3Items}
					errorText={formErrors.dimension3}
					isPersonal={isPersonal}
					disabled={isPersonal}
					isMulti={isMulti}
					dimensionAllocSum={dimension3AllocSum}
					dimensionAllocValid={dimension3AllocValid}
					handleDimensionAllocChange={handleDimensionAllocChange}
					CheckboxOption={CheckboxOption}
					reactSelectSearchSettings={reactSelectSearchSettings}
				/>
			)}

			{dim4 && (
				<DimensionSelectionSection
					t={t}
					dimensionKey={DIMENSION4_KEY}
					dimensionName={dim4?.name ?? ''}
					dimensionId={dim4?.dimensionId ?? ''}
					dimensionOptions={dim4Options}
					selectedItems={selectedDimension4Items}
					setSelectedItems={setSelectedDimension4Items}
					errorText={formErrors.dimension4}
					isPersonal={isPersonal}
					disabled={isPersonal}
					isMulti={isMulti}
					dimensionAllocSum={dimension4AllocSum}
					dimensionAllocValid={dimension4AllocValid}
					handleDimensionAllocChange={handleDimensionAllocChange}
					CheckboxOption={CheckboxOption}
					reactSelectSearchSettings={reactSelectSearchSettings}
				/>
			)}

			{dim5 && (
				<DimensionSelectionSection
					t={t}
					dimensionKey={DIMENSION5_KEY}
					dimensionName={dim5?.name ?? ''}
					dimensionId={dim5?.dimensionId ?? ''}
					dimensionOptions={dim5Options}
					selectedItems={selectedDimension5Items}
					setSelectedItems={setSelectedDimension5Items}
					errorText={formErrors.dimension5}
					isPersonal={isPersonal}
					disabled={isPersonal}
					isMulti={isMulti}
					dimensionAllocSum={dimension5AllocSum}
					dimensionAllocValid={dimension5AllocValid}
					handleDimensionAllocChange={handleDimensionAllocChange}
					CheckboxOption={CheckboxOption}
					reactSelectSearchSettings={reactSelectSearchSettings}
				/>
			)}

			<Form.Group as={Col} xl={5} controlId="dimension6">
				<Form.Label>{t('Organization', 'Organisaatio')}</Form.Label>
				<ReactSelect
					name="dimension6"
					inputId="dimension6"
					onChange={selectChange}
					options={organizationOptions}
					isClearable
					isDisabled={isPersonal}
					value={
						organizationOptions.find((option) => option.value === person.controlDetails?.dimension6) ??
						companyBusinessId
					}
					{...reactSelectSearchSettings(t)}
				/>
				<Form.Control.Feedback type="invalid">{formErrors.dimension6}</Form.Control.Feedback>
			</Form.Group>
		</>
	)
}
